import Address from '@passkeys/ui/address';
import Checkbox from '@passkeys/ui/checkbox';
import Icon from '@passkeys/ui/icon';
import Text from '@passkeys/ui/text';
import TokenIcon from '@passkeys/ui/token-icon';
import { useCallback, useState } from 'react';

import type { Asset } from '@/types';
import ChainBadge from '@/ui/components/chain-badge';
import ListButton from '@/ui/components/list-button/list-button.js';
import exodus from '@/ui/exodus.js';
import useAssets from '@/ui/hooks/use-assets.js';
import useGetAssetDisableBlockReason from '@/ui/hooks/use-get-asset-disable-block-reason';
import useIsEnabledAsset from '@/ui/hooks/use-is-enabled-asset.js';
import { trackMissingIcon } from '@/ui/sentry';
import { openInBlankWindow } from '@/ui/utils/navigation.js';
import TokenItemSpinner from './token-item-spinner.js';
import VerifiedBadge from './verified-badge.js';
import useTokenContractAddress from '../hooks/use-token-contract-address.js';
import useTokenVerified from '../hooks/use-token-verified.js';

const DISABLE_BLOCK_REASON_TO_TEXT = {
  ENABLED_BY_DEFAULT: 'Required by application',
  IT_HAS_BALANCE: 'Balance found',
  IT_HAS_TOKEN_WITH_BALANCE: 'Token with balance found',
};

export interface TokenItemProps {
  token: Asset;
}

function TokenItem({ token }: TokenItemProps) {
  const [toggling, setToggling] = useState(false);

  const assets = useAssets();

  const getAssetDisableBlockReason = useGetAssetDisableBlockReason();

  const enabled = useIsEnabledAsset(token.name);
  const contractAddress = useTokenContractAddress(token);
  const verified = useTokenVerified(token);

  const disableBlockReason = getAssetDisableBlockReason(token);
  const canDisable = !disableBlockReason;

  const handleContractAddressClick = useCallback(
    async (event) => {
      event.stopPropagation(); // Prevents click event from propagating to the row.

      if (contractAddress) {
        const url =
          await token.baseAsset.blockExplorer.addressUrl(contractAddress);
        openInBlankWindow(url);
      }
    },
    [contractAddress, token.baseAsset.blockExplorer],
  );

  const toggle = useCallback(async () => {
    setToggling(true);

    try {
      if (enabled) {
        // Disable asset and its tokens if applicable.
        const assetNames = Object.values(assets).reduce((result, asset) => {
          const {
            name: assetName,
            baseAsset: { name: baseAssetName },
          } = asset;

          if (token.name === assetName || token.name === baseAssetName) {
            result.push(asset.name);
          }

          return result;
        }, [] as string[]);

        await exodus.assets.disable(assetNames);
      } else {
        await (token.assetId
          ? exodus.assets.addAndEnableToken(token.assetId, token.baseAsset.name)
          : exodus.assets.enable([token.name]));
      }
    } finally {
      setToggling(false);
    }

    exodus.analytics.track({
      event: 'WalletAssetManagerToggle',
      properties: {
        assetName: token.name,
        network: token.baseAsset.name,
        toggledOn: !enabled,
      },
    });
  }, [assets, enabled, token.assetId, token.baseAsset.name, token.name]);

  return (
    <ListButton
      dataTestId="manage-tokens-list-item"
      onClick={toggle}
      disabled={disableBlockReason}
    >
      <div className="flex w-full items-center gap-x-3 py-4">
        <TokenIcon
          name={token.name}
          displayName={token.displayName}
          size={40}
          className="flex-none"
          onUnknownName={(name) => trackMissingIcon(name, 'token')}
        />

        <div className="flex flex-1 items-center justify-between gap-x-3 overflow-hidden">
          <div className="flex min-w-0 flex-shrink flex-col">
            <div className="flex items-center gap-x-1">
              <Text
                size={14}
                className="truncate"
                dataTestId="manage-token-name"
              >
                {token.displayName}
              </Text>
              {verified && <VerifiedBadge />}
            </div>
            <Text
              size={14}
              className="truncate text-black/60 theme-dark:text-neutral-100"
              dataTestId="manage-token-ticker"
            >
              {token.displayTicker}
            </Text>
          </div>

          <div className="flex flex-shrink-0 flex-col items-end">
            <ChainBadge name={token.baseAssetName} />

            {contractAddress && !disableBlockReason && (
              <div
                className="mt-1 flex h-4 cursor-pointer items-center gap-x-1 border-b border-transparent text-black hover:border-current theme-dark:text-white"
                data-testid="manage-token-contract-address-button"
                onClick={handleContractAddressClick}
                role="button"
              >
                <Address
                  address={contractAddress}
                  className="text-xs leading-none"
                  dataTestId="manage-token-contract-address"
                  condensed
                />
                <Icon name="arrow-up-right" size={12} />
              </div>
            )}

            {disableBlockReason && (
              <Text
                size={14}
                className="truncate text-black/40 theme-dark:text-neutral-400"
                dataTestId="manage-token-disable-block-reason"
              >
                {DISABLE_BLOCK_REASON_TO_TEXT[disableBlockReason]}
              </Text>
            )}
          </div>
        </div>

        {toggling ? (
          <TokenItemSpinner />
        ) : (
          <Checkbox
            defaultChecked={enabled}
            disabled={!canDisable}
            className="flex-none"
            dataTestId="manage-token-checkbox"
          />
        )}
      </div>
    </ListButton>
  );
}

export default TokenItem;
